import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';
import { Pencil } from 'lucide-react';
import { useEffect, useState } from 'react';

interface DescriptionEditorProps {
  value: string | null;
  /** placeholder 텍스트 — 빈 상태 표시용. */
  placeholder?: string;
  /** 표시 위치 — table description 은 박스, column description 은 inline 컴팩트. */
  variant?: 'block' | 'inline';
  saving?: boolean;
  onSave: (next: string | null) => Promise<unknown> | undefined;
}

export function DescriptionEditor({
  value,
  placeholder = '+ 설명 추가',
  variant = 'block',
  saving,
  onSave,
}: DescriptionEditorProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');

  useEffect(() => {
    if (!editing) setDraft(value ?? '');
  }, [value, editing]);

  const submit = async () => {
    const trimmed = draft.trim();
    await onSave(trimmed.length === 0 ? null : trimmed);
    setEditing(false);
  };

  if (editing) {
    return (
      <div className={cn(variant === 'inline' ? 'space-y-1.5' : 'space-y-2')}>
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={variant === 'inline' ? 2 : 4}
          className={cn(
            'w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-foreground',
            'focus-visible:outline-none',
          )}
          placeholder="LLM 이 이해하기 쉬운 설명을 작성하세요…"
        />
        <div className="flex gap-2 justify-end">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              setEditing(false);
              setDraft(value ?? '');
            }}
            disabled={saving}
          >
            취소
          </Button>
          <Button type="button" size="sm" onClick={submit} disabled={saving}>
            저장
          </Button>
        </div>
      </div>
    );
  }

  if (!value) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        className={cn(
          'inline-flex items-center gap-1.5 text-xs text-foreground-muted hover:text-foreground',
          variant === 'block' ? 'h-8' : 'h-6',
        )}
      >
        {placeholder}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className={cn(
        'group block w-full text-left rounded-md hover:bg-surface-muted/40 transition-colors',
        variant === 'inline'
          ? 'px-1 py-0.5'
          : 'px-3 py-2 border border-transparent hover:border-border',
      )}
    >
      <span
        className={cn(
          'whitespace-pre-wrap',
          variant === 'inline' ? 'text-xs' : 'text-sm leading-relaxed',
        )}
      >
        {value}
      </span>
      <Pencil
        className={cn(
          'inline-block ml-1 size-3 text-foreground-muted opacity-0 group-hover:opacity-100 transition-opacity',
        )}
      />
    </button>
  );
}
